import { nextTick, ref } from 'vue';
import VectorSource from 'ol/source/Vector';
import { Group as LayerGroup, Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import defaultTileLayers from '@/config/defaultTileLayers.js';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';

import { useDefaultsListStore } from '@/stores/defaultsListStore';
import { storeToRefs } from 'pinia';
import Map from 'ol/Map';
import View from 'ol/View';
import { fromLonLat, get as getProjection } from 'ol/proj';
import Overlay from 'ol/Overlay';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import { Draw, Modify, Snap } from 'ol/interaction';
import CircleStyle from 'ol/style/Circle';
import { GeoJSON } from 'ol/format';
import WKT from 'ol/format/WKT';
import { GeometryCollection } from 'ol/geom';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { getTopLeft } from 'ol/extent';
import WMTS from 'ol/source/WMTS';
import TileGrid from 'ol/tilegrid/TileGrid';
import TileWMS from 'ol/source/TileWMS';

import forestreeHelpers from '@/helpers/forestreeHelpers.js';

export const useReportingMapFilter = () => {
    const map = ref({});
    const mapCreated = ref(false);
    const overlay = ref(null);
    const container = ref(null);
    const content = ref(null);
    const closer = ref(null);
    const filterTreesSource = ref();
    const filterTreesLayer = ref(new VectorLayer({ source: filterTreesSource.value }));
    const hexbinLayer = ref(null);
    const polyGeom = ref(null);
    const polygonSource = ref(new VectorSource());
    const polygonVector = ref(new VectorLayer());
    const modify = ref(null);
    const snap = ref(null);
    const draw = ref(null);
    const polygonAdded = ref(false);
    const polygonWkt = ref(null);
    const aerialGroup = ref(null);
    const imageService = ref(new TileLayer({ visible: false }));
    const imageService2 = ref(new TileLayer({ visible: false }));
    const satellite = ref(defaultTileLayers().satelliteTileLayer);
    const osm = ref(defaultTileLayers().osmTileLayer);
    const labels = ref(defaultTileLayers().labelsTileLayer);
    const aerial = ref(defaultTileLayers().aerialTileLayer);

    const defaultsListStore = useDefaultsListStore();
    const { homeCenter } = storeToRefs(defaultsListStore);
    let styleCache = null;

    const createMap = (hasPopup = false,  styleType = 'priority') => {
        const homeLng = parseFloat(homeCenter?.value?.lng || 0);
        const homeLat = parseFloat(homeCenter?.value?.lat || 0);
        const homeZoom = parseFloat(homeCenter?.value?.zoom || 0);

        map.value = new Map({
            target: 'map',
            layers: [osm.value, satellite.value],
            view: new View({
                projection: 'EPSG:3857',
                center: fromLonLat([homeLng, homeLat], 'EPSG:3857'),
                zoom: homeZoom
            })
        });

        if (hasPopup) {
            nextTick().then(() =>{
                container.value = document.getElementById('popup');
                content.value = document.getElementById('popup-content');
                closer.value = document.getElementById('popup-closer');

                overlay.value = new Overlay({
                    element: container.value,
                    autoPan: {
                        animation: { duration: 250 }
                    }
                });
                map.value.addOverlay(overlay.value);
                if(closer.value){
                    closer.value.onclick = () => {
                        overlay.value.setPosition(undefined);
                        closer.value.blur();
                        return false;
                    };

                }
            })
        } else {
            overlay.value = null;
        }
        mapCreated.value = true;

        // polygon setup
        polygonSource.value = new VectorSource();
        polygonVector.value = new VectorLayer({
            source: polygonSource.value,
            style: new Style({
                fill: new Fill({ color: 'rgba(255, 255, 255, 0.1)' }),
                stroke: new Stroke({ color: '#dd6b20', width: 3 }),
                image: new Circle({ radius: 7, fill: new Fill({ color: '#dd6b20' }) })
            })
        });

        map.value.addLayer(polygonVector.value);

        modify.value = new Modify({ source: polygonSource.value });
        draw.value = new Draw({ source: polygonSource.value, type: 'Polygon' });
        snap.value = new Snap({ source: polygonSource.value });

        //region style for openlayer vector layer instead of WebGLPointsLayer
        const getSizeFromZoom = (zoom) => {
            if (zoom <= 2) return 8;
            if (zoom >= 20) return 32;
            return 8 + ((zoom - 2) * (32 - 8)) / (20 - 2);
        };

        filterTreesSource.value = new VectorSource({
            format: new GeoJSON({
                featureProjection: 'EPSG:3857'
            })
        });
        
        let fillStyle = '#2563eb';

        if (styleType === 'risk') {
            // Color by trees_risk_id
            fillStyle = ['match', ['get', 'risk'],
                1, '#22c55e',      // Low Risk - green
                2, '#f59e42',      // Moderate Risk - orange
                3, '#ef4444',      // High Risk - red
                4, '#b91c1c',      // Extreme Risk - dark red
                '#22c55e'          // Default - gray
            ];
        } else if (styleType === 'status') {
            fillStyle = ['match', ['get', 's'],
                1, '#3182ce',
                2, '#4CAF50',
                3, '#718096',
                4, '#e53e3e',
                'rgba(22, 163, 74, 0.7)'
            ];
        } else if (styleType === 'priority') {
            fillStyle = ['match', ['get', 'priority'],
                1, 'rgba(22, 163, 74, 0.7)',
                2, 'rgba(202, 138, 4, 0.7)',
                3, 'rgba(239, 68, 68, 0.7)',
                4, 'rgba(185, 28, 28, 0.7)',
                '#2563eb'
            ];
        }

        // Using WebGLPointsLayer with correct style format
        filterTreesLayer.value = new WebGLPointsLayer({
            source: filterTreesSource.value,
            style: {
                'circle-radius': [
                    'interpolate',
                    ['exponential', 2],
                    ['zoom'],
                    2, 4,
                    20, 32
                ],
                'circle-fill-color': fillStyle,
                'circle-displacement': [0, 0],
                'circle-opacity': 0.7
            }
        });

        map.value.addLayer(filterTreesLayer.value);

        map.value.on('singleclick', e => {
            if (e.dragging) {
                return;
            }
            try {
                const feature = map.value.forEachFeatureAtPixel(e.pixel, (feature) => feature);

                if (hasPopup) {
                    if (feature && feature.values_ && feature.values_.id) {
                        content.value.innerHTML = `
                      <a
                        href="/map?treeId=${feature.values_.id}"
                        target="_blank"
                        class="text-sm leading-5 font-medium text-blue-600 hover:text-blue-700 cursor-pointer"
                      >Tree ID: ${feature.values_.id}</a>
                  `;
                        overlay.value.setPosition(e.coordinate);
                    } else {

                        overlay.value.setPosition(undefined);
                        closer.value.blur();
                    }
                }
            } catch (error) {
                // Handle WebGL context errors gracefully
                console.warn('WebGL context error in singleclick handler:', error);
                if (hasPopup) {
                    overlay.value.setPosition(undefined);
                    closer.value.blur();
                }
            }
        });

        draw.value.on('drawend', (e) => {
            map.value.removeInteraction(draw.value);
            polygonAdded.value = true;
            nextTick(() => {
                setTimeout(() => {
                    updateWkt();
                }, 100);
            });
        });

        modify.value.on('modifyend', (e) => {
            updateWkt();
        });

        // addImagery();
    };

    function enablePolygonDraw () {
        //polygonSource.value.clear();
        map.value.addInteraction(modify.value);
        map.value.addInteraction(draw.value);
        map.value.addInteraction(snap.value);
        const format = new WKT();

        // draw.value.on('drawend', (e) => {
        //     map.value.removeInteraction(draw.value);
        //     polygonAdded.value = true;
        //     const geom = e.feature.getGeometry();
        //     polygonWkt.value = format.writeGeometry(geom);
        //     polyGeom.value = e.feature.getGeometry();
        //     //selectFeatures();
        // });

        // modify.value.on('modifyend', (e) => {
        //     polygonWkt.value = format.writeGeometry(e.features.getArray()[0].getGeometry());
        //     polyGeom.value = e.features.getArray()[0].getGeometry();
        //     //selectFeatures();
        // });
    }

    function updateWkt () {
        var format = new WKT();
        var geomAll = polygonSource.value.getFeatures();
        let geomC = new GeometryCollection(geomAll.map((f) => f.getGeometry()));
        if (format.writeGeometry(geomC) === 'GEOMETRYCOLLECTION EMPTY') {
            polygonWkt.value = null;
        } else {
            polygonWkt.value = format.writeGeometry(geomC);
        }
    }

    function addFeatureToMap (feature) {
        polygonSource.value.addFeature(new WKT().readFeature(feature));
        updateWkt();
    }

    function restart () {
        map.value.addInteraction(draw.value);
        polygonSource.value.clear();
        polygonAdded.value = false;
        polygonWkt.value = null;
        polyGeom.value = null;
    }

    function addTreesGL (trees) {
        // Ensure the layer is present on the map
        if (filterTreesLayer.value && map.value && !map.value.getLayers().getArray().includes(filterTreesLayer.value)) {
            map.value.addLayer(filterTreesLayer.value);
        }
        const obj = JSON.parse(JSON.stringify(trees));
        filterTreesSource.value.clear();
        filterTreesSource.value.addFeatures((new GeoJSON({
            featureProjection: 'EPSG:3857'
        })).readFeatures(obj));
        map.value.render();
    }

    /**
     * Removes the hexbin layer from the map if it exists.
     */
    function removeHexbinLayer() {
        if (hexbinLayer.value && map.value.getLayers().getArray().includes(hexbinLayer.value)) {
            map.value.removeLayer(hexbinLayer.value);
            hexbinLayer.value = null;
        }
    }

    /**
     * Adds a hexbin layer to the map, aggregating features by their 'latest_val' property.
     * @param {Array|Object} trees - Array of tree features (GeoJSON or OL features)
     * @param {number} [hexRadius=200] - Hex radius in map units (meters)
     */
    function addHexbinLayer(trees, hexRadius = 200) {
        // Remove previous hexbin layer if exists
        if (hexbinLayer.value) {
            map.value.removeLayer(hexbinLayer.value);
            hexbinLayer.value = null;
        }
        // Remove previous hexbin overlay if exists
        if (map.value && map.value.hexbinTooltipOverlay) {
            map.value.removeOverlay(map.value.hexbinTooltipOverlay);
            map.value.hexbinTooltipOverlay = null;
        }
        // Convert input to OL features
        const obj = JSON.parse(JSON.stringify(trees));
        let features;
        // If array of raw objects, convert to OL features
        features = (new GeoJSON({ featureProjection: 'EPSG:3857' })).readFeatures(obj);

        // Get all coordinates and their latest_val
        const points = features.map(f => {
            const latestVal = f.getProperties().latest_val;
            const geom = f.getGeometry();
            if (!geom || geom.getType() !== 'Point') return null;
            const coord = geom.getCoordinates();
            const val = f.get('latest_val') || 0;
            return { coord, val };
        }).filter(Boolean);
        if (points.length === 0) return;
        // Calculate hexbin grid bounds
        const xs = points.map(p => p.coord[0]);
        const ys = points.map(p => p.coord[1]);
        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        // Hexagon math
        const hexWidth = hexRadius * 2;
        const hexHeight = Math.sqrt(3) * hexRadius;
        // Bin points into hexes
        const hexMap = {};
        for (let p of points) {
            // Calculate hex grid coordinates (axial)
            const q = Math.round((p.coord[0] - minX) / (hexRadius * 3/2));
            const r = Math.round(((p.coord[1] - minY) / hexHeight) - (q % 2 ? 0.5 : 0));
            const key = `${q},${r}`;
            if (!hexMap[key]) hexMap[key] = [];
            hexMap[key].push(p.val);
        }
        // Create hexagon features
        const hexFeatures = [];
        for (let key in hexMap) {
            const vals = hexMap[key];
            const [q, r] = key.split(',').map(Number);
            // Center of hex
            const cx = minX + hexRadius * 3/2 * q;
            const cy = minY + hexHeight * (r + 0.5 * (q % 2));
            // Build hexagon polygon
            const coords = [];
            for (let i = 0; i < 6; i++) {
                const angle = Math.PI / 3 * i;
                coords.push([
                    cx + hexRadius * Math.cos(angle),
                    cy + hexRadius * Math.sin(angle)
                ]);
            }
            coords.push(coords[0]);
            // Aggregate value (sum)
            const aggVal = vals.reduce((a, b) => a + b, 0);
            // Create feature
            const hexFeature = new Feature({
                geometry: new Polygon([coords]),
                count: vals.length,
                sum: aggVal,
                avg: aggVal / vals.length,
            });
            hexFeatures.push(hexFeature);
        }
        // Create vector source/layer
        const hexSource = new VectorSource({ features: hexFeatures });
        // Color scale: yellow gradient (light to deep yellow)
        const maxSum = Math.max(...hexFeatures.map(f => f.get('sum')));
        function getYellowGradient(val) {
            // t: 0 (min) -> 1 (max)
            const t = maxSum ? val / maxSum : 0;
            // Interpolate from light yellow (rgb(255,255,204)) to deep yellow (rgb(255,204,0))
            const r = 255;
            const g = Math.round(255 - 51 * t); // 255 -> 204
            const b = Math.round(204 - 204 * t); // 204 -> 0
            return `rgba(${r},${g},${b},0.7)`;
        }
        const hexStyle = feature => {
            return new Style({
                fill: new Fill({ color: getYellowGradient(feature.get('sum')) }),
                stroke: new Stroke({ color: 'rgba(255, 255, 255, 0.8)', width: 2 }),
            });
        };
        hexbinLayer.value = new VectorLayer({
            source: hexSource,
            style: hexStyle,
            zIndex: 20
        });
        map.value.addLayer(hexbinLayer.value);

        // --- Add hover tooltip overlay for hexbin ---
        // Create tooltip element
        let tooltipEl = document.getElementById('hexbin-tooltip');
        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'hexbin-tooltip';
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.background = 'rgba(255,255,204,0.95)';
            tooltipEl.style.border = '1px solid #b59f3b';
            tooltipEl.style.padding = '4px 8px';
            tooltipEl.style.borderRadius = '4px';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.whiteSpace = 'nowrap';
            tooltipEl.style.fontSize = '13px';
            tooltipEl.style.fontWeight = 'bold';
            tooltipEl.style.color = '#7a5c00';
            tooltipEl.style.zIndex = 1000;
            document.body.appendChild(tooltipEl);
        }
        tooltipEl.style.display = 'none';

        // Create OpenLayers overlay for tooltip
        const tooltipOverlay = new Overlay({
            element: tooltipEl,
            offset: [10, 0],
            positioning: 'center-left',
            stopEvent: false
        });
        map.value.addOverlay(tooltipOverlay);
        map.value.hexbinTooltipOverlay = tooltipOverlay;

        // Pointermove handler
        function pointerMoveHandler(evt) {
            if (!evt.dragging) {
                try {
                    const pixel = map.value.getEventPixel(evt.originalEvent);
                    let feature = map.value.forEachFeatureAtPixel(pixel, (f, l) => {
                        if (l === hexbinLayer.value) return f;
                    });
                    if (feature && feature.get('sum') !== undefined) {
                        const coordinate = evt.coordinate;
                        tooltipOverlay.setPosition(coordinate);
                        tooltipEl.innerHTML =
                            `<span>Total Valuation: <b>${forestreeHelpers.centsToDollar(feature.get('sum'))}</b></span><br>` +
                            `<span>Avg Valuation: <b>${forestreeHelpers.centsToDollar(feature.get('avg'))}</b></span><br>` +
                            `<span>Trees count: <b>${feature.get('count')}</b></span>`;
                        tooltipEl.style.display = 'block';
                    } else {
                        tooltipEl.style.display = 'none';
                        tooltipOverlay.setPosition(undefined);
                    }
                } catch (error) {
                    // Handle WebGL context errors gracefully
                    console.warn('WebGL context error in pointerMoveHandler:', error);
                    tooltipEl.style.display = 'none';
                    tooltipOverlay.setPosition(undefined);
                }
            }
        }
        // Remove any previous pointermove listeners
        if (map.value.hexbinPointerMoveKey) {
            map.value.un('pointermove', map.value.hexbinPointerMoveKey);
            map.value.hexbinPointerMoveKey = null;
        }
        // Add pointermove listener
        map.value.hexbinPointerMoveKey = pointerMoveHandler;
        map.value.on('pointermove', pointerMoveHandler);
    }

    function addImagery () {
        const extent = [265000, 4400000, 800000, 5000000];
        const resolutions = [260.0, 130.0, 65.0, 32.5, 16.25, 8.125, 4.0625, 2.03125, 1.015625];
        const tileGrid = new WMTSTileGrid({
            origin: getTopLeft(extent),
            resolutions: resolutions,
            matrixIds: resolutions.map((r, i) => `EPSG:3401:${i}`)
        });

        const source = new WMTS({
            url: 'https://tiles.arcgis.com/tiles/rTnErLtPyRk8UhaN/arcgis/rest/services/DRONE_test_001/MapServer/WMTS?',
            layer: 'DRONE_test_001',
            matrixSet: 'EPSG:3401',
            format: 'image/png',
            projection: getProjection('EPSG:3401'),
            tileGrid: tileGrid,
            style: 'default',
            wrapX: true
        });

        imageService.value = new TileLayer({ source: source, extent: extent, visible: false });
        map.value.addLayer(imageService.value);

        const tileGrid2 = new TileGrid({
            origin: getTopLeft(extent),
            resolutions: resolutions
        });

        imageService2.value = new TileLayer({
            source: new TileWMS({
                url: 'https://tiles.arcgis.com/tiles/rTnErLtPyRk8UhaN/arcgis/rest/services/DRONE_test_002/MapServer/WMTS?',
                params: { LAYERS: 'DRONE_test_002', TILED: true },
                serverType: 'geoserver',
                tileGrid: tileGrid2
            }),
            extent: extent,
            visible: false
        });

        map.value.addLayer(imageService2.value);

        aerialGroup.value = new LayerGroup({
            layers: [imageService.value, imageService2.value],
            visible: false
        });

        map.value.addLayer(aerialGroup.value);
    }

    /**
     * Removes the tree points layer created by addTreesGL from the map.
     */
    function removeTreesGL() {
        if (filterTreesLayer.value && map.value && map.value.getLayers().getArray().includes(filterTreesLayer.value)) {
            map.value.removeLayer(filterTreesLayer.value);
        }
    }

    return {
        map,
        mapCreated,
        overlay,
        container,
        content,
        closer,
        filterTreesSource,
        filterTreesLayer,
        polyGeom,
        polygonSource,
        polygonVector,
        modify,
        snap,
        draw,
        polygonAdded,
        polygonWkt,
        aerialGroup,
        imageService,
        imageService2,
        satellite,
        osm,
        labels,
        aerial,
        createMap,
        enablePolygonDraw,
        updateWkt,
        addFeatureToMap,
        restart,
        addTreesGL,
        removeTreesGL,
        addHexbinLayer,
        removeHexbinLayer,
        addImagery
    };

};